import { Box, Button, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchCoachAvailabilitiesForMiniweb,
  getAvailableTimesRequest,
} from '../../redux/miniweb/miniwebSlice';
import { COACH_PROFILE_ROUTE, COACH_PROFILE_SET_BOOKING_DETAILS_ROUTE } from '../../utils/routes';
import { formatDuration } from '../../utils/utils';
import MiniWebSiteHeader from './MiniWebSiteHeader/MiniWebSiteHeader';
import MiniWebSiteFooter from './MiniWebSiteFooter/MiniWebSiteFooter';

const MiniWebSiteSetBookingTime = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { username } = useParams();
  const { servicePackage, selectedDate } = location.state || {};
  const availableTimes = useAppSelector((state) => state.miniweb.availableTimes) || [];
  useEffect(() => {}, [availableTimes]);
  const { getCoachUnavalabilitiesForDayLoading } = useAppSelector((state) => state.miniweb);
  const isArabic = i18n.language === 'ar';

  useEffect(() => {
    if (username) {
      dispatch(fetchCoachAvailabilitiesForMiniweb({ username }));
    }
  }, [dispatch, username]);

  useEffect(() => {
    if (username && selectedDate) {
      dispatch(
        getAvailableTimesRequest({
          username,
          date: dayjs(selectedDate?.$d).get('date'),
          month: dayjs(selectedDate?.$d).get('month'),
          year: dayjs(selectedDate?.$d).get('year'),
          duration: servicePackage.sessionDuration,
        })
      );
    }
  }, [dispatch, username, selectedDate]);

  const [startAt, setStartAt] = useState<Date | null>(null);

  useEffect(() => {
    if (username && (!servicePackage || !selectedDate)) {
      navigate(COACH_PROFILE_ROUTE.replace(':username', username));
    }
  }, [servicePackage, selectedDate, username, navigate]);

  const handleIntervalClick = (interval: Date) => {
    setStartAt(interval === startAt ? null : interval);
  };

  const handleNavigateToBookingDetails = () => {
    if (startAt && username && servicePackage && selectedDate) {
      navigate(COACH_PROFILE_SET_BOOKING_DETAILS_ROUTE.replace(':username', username), {
        state: {
          username,
          servicePackage,
          startAt,
          duration: servicePackage.sessionDuration,
        },
      });
    }
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const renderIntervals = () => {
    if (getCoachUnavalabilitiesForDayLoading) {
      return <CircularProgress />;
    }

    if (availableTimes.length === 0) {
      return (
        <Typography variant="bodyBold" color="#2A3C67">
          {t('miniWebSite.booking.noSlotsAvailable')}
        </Typography>
      );
    }

    return availableTimes.map((interval) => (
      <Button
        key={interval.valueOf()}
        variant="outlined"
        onClick={() => handleIntervalClick(interval)}
        sx={{
          width: '100%',
          height: '46px',
          padding: '12px 0',
          gap: '4px',
          borderRadius: '5px',
          border: '1px solid #7CAEBE80',
          backgroundColor: startAt === interval ? '#FFDDC8' : 'transparent',
          '&:hover': {
            backgroundColor: startAt === interval ? '#FFDDC8' : 'transparent',
          },
        }}
      >
        {dayjs(interval).format('hh:mm A')}
      </Button>
    ));
  };

  return (
    <Grid
      container
      display="flex"
      sx={{
        background: 'linear-gradient(163deg, #FFF 10.13%, #FFF4F0 89.18%)',
        minHeight: '100vh',
        flexDirection: 'column',
      }}
      alignItems="center"
      justifyContent="center"
    >
      <MiniWebSiteHeader />
      {servicePackage && selectedDate && (
        <Grid
          item
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          mt={5}
          mb={5}
          sx={{ flexGrow: 1, gap: '18px' }}
        >
          <Box
            sx={{
              width: '361px',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0px 4px 40px 0px #FFDDC840',
              borderRadius: '10px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '99px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                borderBottom: '1px solid #EEEEEE',
                padding: '0 16px',
              }}
            >
              <Button
                onClick={handleNavigateBack}
                sx={{
                  width: '10px',
                  height: '24px',
                  background: 'transparent !important',
                }}
              >
                <img src="/assets/icons/back-arrow.svg" alt="back-arrow" height={24} width={24} />
              </Button>
              <Box
                sx={{
                  width: '285.67px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginRight: isArabic ? '0px' : '65px',
                  marginLeft: isArabic ? '65px' : '0px',
                }}
              >
                <Tooltip
                  title={servicePackage.title}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#2A3C67',
                        color: 'white',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    color="#2A3C67"
                    maxWidth="200px"
                    sx={{
                      cursor: 'pointer',
                      flexGrow: 1,
                      marginLeft: '8px',
                      marginRight: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    {servicePackage.title}
                  </Typography>
                </Tooltip>
                <Typography variant="bodyBold" color="#2A3C67">
                  {dayjs(selectedDate.$d).format('DD MMM YYYY')}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Typography variant="bodyBold" color="#2A3C67">
                {t('miniWebSite.booking.selectTime')}
              </Typography>
              <Typography variant="meta" color="#2A3C67">
                {t('miniWebSite.booking.duration')} :{' '}
                {formatDuration(servicePackage.sessionDuration)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '329px',
                height: '382px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '16px',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              {renderIntervals()}
            </Box>
            <Box
              sx={{
                height: '46px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <Button
                variant="contained"
                sx={{
                  background:
                    'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
                  borderRadius: '15px',
                  '&:hover': {
                    background:
                      'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
                  },
                  marginTop: 'auto',
                }}
                disabled={!startAt}
                onClick={handleNavigateToBookingDetails}
              >
                {t('miniWebSite.booking.next')}
              </Button>
            </Box>
          </Box>
        </Grid>
      )}
      <MiniWebSiteFooter />
    </Grid>
  );
};

export default MiniWebSiteSetBookingTime;
